import type { DocumentEditorMode } from "@/components/artifacts/DocumentEditor";
import type { AppLocationCodec } from "@/navigation/appLocation";
import { toAbsoluteUrl } from "@/navigation/absoluteUrl";
import { NAV_KEYS } from "@/navigation/keys";

export type ArtifactSurfaceView = "handover-approval";

export type ArtifactLocation = {
  id: string;
  mode?: DocumentEditorMode;
  taskId?: string;
  view?: ArtifactSurfaceView;
  clientName?: string;
};

const ARTIFACT_MODES = new Set<DocumentEditorMode>(["read", "comment", "edit", "review"]);

const ARTIFACT_PATH_SURFACES: ReadonlySet<string> = new Set(["handover-approval"]);

function parseMode(raw: string | null | undefined): DocumentEditorMode | undefined {
  if (raw && ARTIFACT_MODES.has(raw as DocumentEditorMode)) {
    return raw as DocumentEditorMode;
  }
  return undefined;
}

function readTaskId(params: Record<string, string | undefined>): string | undefined {
  const canonical = params[NAV_KEYS.taskId]?.trim();
  if (canonical) return canonical;
  const legacy = params[NAV_KEYS.taskLegacy]?.trim();
  return legacy || undefined;
}

function parseSurfaceFromPath(pathname: string): ArtifactSurfaceView | undefined {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] === "artifacts" && segments[2] === "handover-approval") {
    return "handover-approval";
  }
  return undefined;
}

function normalize(loc: ArtifactLocation): ArtifactLocation {
  const next: ArtifactLocation = { id: loc.id };
  if (loc.mode) next.mode = loc.mode;
  if (loc.taskId) next.taskId = loc.taskId;
  if (loc.view) next.view = loc.view;
  if (loc.clientName) next.clientName = loc.clientName;
  return next;
}

function parseIdFromPath(pathname: string): string | undefined {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] === "artifacts" && segments[1]) {
    return decodeURIComponent(segments[1]);
  }
  return undefined;
}

function fromParams(params: Record<string, string | undefined>): ArtifactLocation {
  const id = params.id?.trim() ?? "";
  const loc: ArtifactLocation = { id: id || "unknown" };
  const mode = parseMode(params[NAV_KEYS.mode]);
  if (mode) loc.mode = mode;
  const taskId = readTaskId(params);
  if (taskId) loc.taskId = taskId;
  if (params.id?.trim()) loc.id = params.id.trim();
  const viewRaw = params[NAV_KEYS.view]?.trim();
  if (viewRaw && ARTIFACT_PATH_SURFACES.has(viewRaw)) {
    loc.view = viewRaw as ArtifactSurfaceView;
  }
  const clientName = params.clientName?.trim();
  if (clientName) loc.clientName = clientName;
  return normalize(loc);
}

function fromUrl(pathname: string, searchParams: URLSearchParams): ArtifactLocation {
  const params: Record<string, string | undefined> = Object.fromEntries(searchParams);
  const pathId = parseIdFromPath(pathname);
  if (pathId) params.id = pathId;
  const pathView = parseSurfaceFromPath(pathname);
  if (pathView) {
    params[NAV_KEYS.view] = pathView;
  }
  return fromParams(params);
}

function toParams(loc: ArtifactLocation): Record<string, string> {
  const n = normalize(loc);
  const out: Record<string, string> = { id: n.id };
  if (n.mode) out[NAV_KEYS.mode] = n.mode;
  if (n.taskId) out[NAV_KEYS.taskId] = n.taskId;
  if (n.view) out[NAV_KEYS.view] = n.view;
  if (n.clientName) out.clientName = n.clientName;
  return out;
}

function toUrl(loc: ArtifactLocation): string {
  const n = normalize(loc);
  const { id, ...query } = toParams(n);
  if (n.view === "handover-approval") {
    const path = `/artifacts/${encodeURIComponent(id)}/handover-approval`;
    const qs = new URLSearchParams();
    if (n.taskId) qs.set(NAV_KEYS.taskId, n.taskId);
    if (n.clientName) qs.set("clientName", n.clientName);
    const qsStr = qs.toString();
    return qsStr ? `${path}?${qsStr}` : path;
  }
  const pathname = `/artifacts/${encodeURIComponent(id)}`;
  const filtered: Record<string, string> = { ...query };
  delete filtered.id;
  delete filtered[NAV_KEYS.view];
  const qs = new URLSearchParams(filtered).toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export const artifactCodec: AppLocationCodec<ArtifactLocation> = {
  appId: "artifact",
  fromUrl,
  fromParams,
  toParams,
  toUrl,
};

export function buildArtifactWindowUrl(
  artifactId: string,
  patch: Partial<Omit<ArtifactLocation, "id">> = {},
): string {
  return artifactCodec.toUrl({ id: artifactId, ...patch });
}

export function artifactShareUrl(
  artifactId: string,
  patch: Partial<Omit<ArtifactLocation, "id">> = {},
): string {
  return toAbsoluteUrl(buildArtifactWindowUrl(artifactId, patch));
}
